import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import { fetchKitchens as fetchPublicKitchens } from "../../../services/catalog.api";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import "../styles/PreOrderFoodParent.css";
import KitchenCard from "../cards/KitchenCard";

const swiperBreakpoints = {
  1400: { slidesPerView: 5, spaceBetween: 16 },
  1200: { slidesPerView: 4, spaceBetween: 14 },
  992: { slidesPerView: 3, spaceBetween: 12 },
  576: { slidesPerView: 2, spaceBetween: 10 },
};

const arrowStyle = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: "1px solid #e5e7eb",
  backgroundColor: "#ffffff",
  color: "#111827",
  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
  zIndex: 20,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "box-shadow 0.15s, background-color 0.15s",
  flexShrink: 0,
};

// Fetch kitchens from API
const fetchKitchens = () => fetchPublicKitchens({ limit: 20 });

export default function Kitchens() {
  const swiperRef = useRef(null);
  const {
    data = [],
    isPending,
    error,
  } = useQuery({
    queryKey: ["kitchens"],
    queryFn: fetchKitchens,
  });
  if (error) {
    toast.error(error?.message);
    return null;
  }

  if (data?.length === 0 && !isPending) return null;

  return (
    <div
      className="container home-content-shell"
      style={{ paddingTop: 16, marginBottom: 24 }}
    >
      {/* Section header */}
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: 12, paddingLeft: 4, paddingRight: 4 }}
      >
        <h5
          style={{
            fontWeight: 800,
            fontSize: 18,
            margin: 0,
            fontFamily: "'Inter', sans-serif",
            color: "#1a1a2e",
          }}
        >
          Kitchens
        </h5>
        <div
          style={{
            fontSize: 13,
            color: "#1b6dc1",
            textDecoration: "none",
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          <Link to="/all-kitchens" className="see-more-link">
            See More
          </Link>
        </div>
      </div>

      {/* Swiper container */}
      <div style={{ position: "relative" }}>
        <Swiper
          modules={[Navigation, Autoplay]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          slidesPerView={1.3}
          spaceBetween={10}
          loop={data?.length > 5}
          autoplay={{ delay: 2000, disableOnInteraction: false }}
          speed={500}
          breakpoints={swiperBreakpoints}
          style={{ paddingBottom: 8 }}
        >
          {data?.map((item) => (
            <SwiperSlide key={item?._id}>
              <KitchenCard item={item} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Nav arrows */}
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          style={{ ...arrowStyle, left: -6 }}
          aria-label="Previous slide"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)";
          }}
        >
          <ArrowLeft size={14} strokeWidth={2.2} />
        </button>

        <button
          onClick={() => swiperRef.current?.slideNext()}
          style={{ ...arrowStyle, right: -6 }}
          aria-label="Next slide"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)";
          }}
        >
          <ArrowRight size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
